import { SolaView, htmlSolaWithInstance } from '@hm-spt/sola';

import { escapeHtml } from '../lib/cells';
import type { TableMetaFromServer } from '../types';

interface CatalogSidebarData {
  tables: TableMetaFromServer[];
  currentTable: string;
  apiStatus: 'unknown' | 'ok' | 'err';
  apiMessage: string;
}

/**
 * 🎯 CatalogSidebar — /catalog 페이지 내부 사이드바 (28 테이블/뷰)
 *
 * 각 테이블 항목이 <a data-link href="/catalog/:tableName"> 형태라서
 * 라우터가 클릭을 자동 인터셉트 — sidebar 가 라우터 인스턴스를 알 필요 X.
 */
export class CatalogSidebar extends SolaView<CatalogSidebarData> {
  constructor() {
    super();
    this.data = {
      tables: [],
      currentTable: '',
      apiStatus: 'unknown',
      apiMessage: 'API 확인 중…',
    };
  }

  setTables(tables: TableMetaFromServer[]): void {
    this.data.tables = tables;
    this.rerenderNav();
  }

  setCurrent(name: string): void {
    this.data.currentTable = name;
    this.rerenderNav();
  }

  setApiStatus(status: 'ok' | 'err', message: string): void {
    this.data.apiStatus = status;
    this.data.apiMessage = message;
    this.rerenderStatus();
  }

  render(): HTMLElement {
    return htmlSolaWithInstance(this)`
      <aside class="catalog-sidebar">
        <header class="catalog-sidebar-header">
          <span class="catalog-sidebar-title">📚 카탈로그</span>
        </header>
        <nav class="catalog-sidebar-nav">
          <div class="sidebar-loading">불러오는 중…</div>
        </nav>
        <footer class="catalog-sidebar-footer">
          <span class="api-status">
            <span class="dot"></span>
            <span class="api-status-text">API 확인 중…</span>
          </span>
        </footer>
      </aside>
    `;
  }

  private rerenderNav(): void {
    const nav = this._rootElement?.querySelector('.catalog-sidebar-nav');
    if (!nav) return;

    if (this.data.tables.length === 0) {
      nav.innerHTML = '<div class="sidebar-loading">불러오는 중…</div>';
      return;
    }

    const groups = new Map<string, TableMetaFromServer[]>();
    for (const t of this.data.tables) {
      const arr = groups.get(t.group) ?? [];
      arr.push(t);
      groups.set(t.group, arr);
    }

    const out: string[] = [];

    // 🔍 전체 검색 — 모든 그룹 위에 sticky 고정 항목
    const searchActive = this.data.currentTable === '_search' ? ' active' : '';
    out.push(
      `<div class="nav-group nav-group-search">
        <a class="nav-item nav-item-search${searchActive}" data-link href="/catalog/_search">
          <span class="nav-label">🔍 전체 검색</span>
        </a>
      </div>`,
    );

    for (const [group, items] of groups) {
      out.push(
        `<div class="nav-group"><div class="nav-group-title">${escapeHtml(group)}</div>`,
      );
      for (const t of items) {
        const active = t.name === this.data.currentTable ? ' active' : '';
        out.push(
          `<a class="nav-item${active}" data-link href="/catalog/${encodeURIComponent(t.name)}">
            <span class="nav-label">${escapeHtml(t.label || t.name)}</span>
          </a>`,
        );
      }
      out.push('</div>');
    }
    nav.innerHTML = out.join('');
  }

  private rerenderStatus(): void {
    const wrap = this._rootElement?.querySelector('.api-status');
    if (!wrap) return;
    wrap.classList.remove('ok', 'err');
    if (this.data.apiStatus === 'ok') wrap.classList.add('ok');
    if (this.data.apiStatus === 'err') wrap.classList.add('err');
    const text = wrap.querySelector('.api-status-text');
    if (text) text.textContent = this.data.apiMessage;
  }
}
